<script setup>
import AppLink from '@/Shared/Components/Links/AppLink.vue'
import SecondaryButton from '@/Shared/Components/Buttons/SecondaryButton.vue'
import { ref } from 'vue'

const props = defineProps({
  sortColumn: String,
  sortDirection: String,
  columns: {
    type: Array,
    default: () => [],
  },
  rows: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isSticky: {
    type: Boolean,
    default: false,
  },
  isLite: {
    type: Boolean,
    default: false,
  },
  visibleRow: {
    type: Number,
    default: 9999,
  },
})

const emits = defineEmits(['update:sortColumn', 'update:sortDirection'])

const setActiveSort = (column) => {
  emits('update:sortColumn', column.slug)
  emits('update:sortDirection', props.sortDirection == 'desc' ? 'asc' : 'desc')
}

const visibleRowCount = ref(props.visibleRow)

// Function to show all rows
const showAllRows = () => {
  visibleRowCount.value = props.rows.length // Set to total number of rows
}
</script>

<template>
  <slot name="header" />

  <div v-if="rows.length < 1">
    <!-- <slot name="zero-data">
      <ZeroData />
    </slot> -->
  </div>
  <div
    v-else
    class="relative overflow-x-auto mb-5"
  >
    <table
      class="min-w-full table-auto"
      :class="isLite ? '' : 'border-t border-table-border'"
    >
      <slot
        name="loading"
        :loading="loading"
      >
        <!-- <Spinner v-show="loading" /> -->
      </slot>
      <thead>
        <tr
          :class="
            isLite
              ? ''
              : 'divide-x divide-table-border first:border-l first:border-table-border last:border-r last:border-table-border'
          "
        >
          <th
            v-for="(column, index) in columns"
            :key="column.id ? column.id : index"
            class="whitespace-nowrap py-2 text-lg font-bold"
            :class="[isLite ? '' : 'bg-table-primary', column.class ?? '']"
            :aria-sort="
              column.sortable && column.slug === sortColumn ? (sortDirection == 'asc' ? 'ascending' : 'descending') : ''
            "
          >
            <slot
              name="column"
              :index="index"
              :column="column"
            >
              <AppLink
                v-if="column.sortable"
                as="button"
                class="relative mx-2 flex items-center py-0 pr-3 no-underline hover:bg-transparent"
                :text="column.name"
                @click="setActiveSort(column)"
              />
              <div
                v-else
                class="flex items-center"
              >
                <span class="mr-1">{{ column.name }}</span>
              </div>
            </slot>
          </th>
        </tr>
      </thead>
      <tbody :class="isLite ? '' : 'border border-table-border'">
        <tr
          v-for="(row, index) in rows.slice(0, visibleRowCount)"
          :key="row.id ? row.id : index"
          :class="
            isLite
              ? [index < rows.length - 1 ? 'border-b' : '', 'border-table-border']
              : { 'bg-table-primary-light': index % 2 === 1 }
          "
        >
          <slot
            :row="row"
            :index="index"
          />
        </tr>
      </tbody>
    </table>
  </div>
  <div class="text-center">
    <SecondaryButton
      v-if="visibleRowCount < rows.length"
      class="mt-4 text-primary mb-4"
      @click="showAllRows"
    >
      Show All Rows
    </SecondaryButton>
  </div>
  <div
    aria-live="polite"
    role="status"
    aria-atomic="true"
    class="sr-only"
  >
    Sort by {{ sortColumn }} ({{ sortDirection == 'asc' ? 'ascending' : 'descending' }})
  </div>
</template>

<style scoped>
.npads-table__header[aria-sort] button:before {
  content: ' \25bc';
  @apply absolute right-0 top-[8px] text-[0.5em];
}

.npads-table__header[aria-sort] button:after {
  content: ' \25b2';
  @apply absolute right-0 top-0 text-[0.5em];
}

.npads-table__header[aria-sort='ascending'] button:after {
  content: ' \25b2';
  @apply absolute -right-[2px] top-[4px] text-[0.8em];
}

.npads-table__header[aria-sort='descending'] button:after {
  content: ' \25bc';
  @apply absolute -right-[2px] top-[5px] text-[0.8em];
}

.npads-table__header[aria-sort='ascending'] button:before,
.npads-table__header[aria-sort='descending'] button:before {
  content: none;
}
</style>
